import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatDisplayDate } from "@/lib/dateFormat";

export interface QuoteDeadlineChipProps {
  /** ISO timestamp the quoting window closes. */
  expiresAt: string;
  className?: string;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whole calendar days from today to the deadline's date, in the reader's own
 * timezone.
 *
 * The chip prints the date next to the count, so the two have to agree: on the
 * 6th, a deadline of the 10th is "4 días" regardless of the hour either falls
 * at. Counting elapsed time instead makes the count depend on the time of day
 * the window happened to be opened, which is invisible to the reader and looks
 * like a bug.
 *
 * Rounded rather than floored after dropping to midnight: a DST transition
 * makes one of those days 23 or 25 hours long, and the division would
 * otherwise land just under or over an integer.
 *
 * Pinned by quote-deadline-chip.spec.ts, which restates this function because
 * importing a .tsx would drag react-i18next and lucide into a node script.
 * Change one and change the other.
 */
function calendarDaysUntil(expiresAt: string): number {
  const end = new Date(expiresAt);
  const now = new Date();
  if (Number.isNaN(end.getTime())) return 0;
  const endMidnight = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((endMidnight - todayMidnight) / MS_PER_DAY);
}

/**
 * The quoting deadline, as a deadline.
 *
 * It used to render as a bold sentence under the request description, which
 * put a hard constraint in the same visual register as prose and made "13 sep"
 * something the professional had to convert to "how long have I got" on their
 * own. A chip reads in one glance, and the days remaining — not the date — is
 * the number that decides whether they bid now or later.
 *
 * Urgency is carried by color only once it means something: neutral while
 * there is time, amber inside the last two days, destructive once closed.
 * Colouring every deadline red would make the colour stop meaning "hurry".
 */
export function QuoteDeadlineChip({ expiresAt, className }: QuoteDeadlineChipProps): JSX.Element {
  const { t } = useTranslation();

  const msLeft = new Date(expiresAt).getTime() - Date.now();
  // Closure is a real instant, not a calendar day: a window ending at 15:44
  // is shut at 16:00, whatever the date says.
  const isClosed = msLeft <= 0;
  // CALENDAR days, not elapsed ones, because the date is printed right beside
  // this. Math.ceil on the raw difference counted a window ending on the 10th
  // at 15:44 as "5 días" on the morning of the 6th — 4.3 elapsed days rounded
  // up — so the chip read "closes in 5 days · 10 oct" on the 6th and did its
  // own arithmetic wrong in front of the reader. Flooring would have been just
  // as wrong in the other direction on a window ending late in the day.
  //
  // Both ends are dropped to local midnight and the difference rounded, so a
  // DST transition (a 23- or 25-hour day) cannot shift the count by one.
  const daysLeft = calendarDaysUntil(expiresAt);
  const isUrgent = !isClosed && daysLeft <= 2;

  const label = isClosed
    ? t("requests.scope.deadlineClosed")
    : daysLeft <= 0
      ? t("requests.scope.deadlineToday")
      : t("requests.scope.deadlineDays", { count: daysLeft });

  return (
    <span
      className={cn(
        "mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        isClosed
          ? "border-destructive/30 bg-destructive/10 text-destructive"
          : isUrgent
            ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
            : "border-border bg-muted text-muted-foreground",
        className,
      )}
    >
      <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {label}
      <span className="font-normal opacity-70">· {formatDisplayDate(expiresAt)}</span>
    </span>
  );
}
